/**
 * The Markdown subsets Connect's documents are written in, parsed into plain data.
 *
 * Two dialects of one parser:
 *
 * - **`legal`** (the default) — headings, paragraphs, bulleted and numbered
 *   lists, block quotes, rules, bold, italic and links. That covers a Terms of
 *   Service. It is also exactly what the mobile app parses (`legal_text.dart`),
 *   and the two must agree on what a document a client accepts says — so this
 *   dialect never grows without the mobile parser growing with it.
 * - **`guide`** — the legal subset, plus `inline code`, fenced code blocks and
 *   pipe tables: what a setup guide needs to name a field, show a request, and
 *   lay out "Meta says / meaning". The product's docs (`GET /docs`) use it.
 *
 * Both leave out raw HTML above all — anything that could make the text shown
 * differ from the text written, or run in the reader's page. The output is data,
 * rendered by `DocumentText` as React elements, so no string from a document
 * ever reaches `innerHTML`. A `<script>` in a document is shown as the characters
 * `<script>`. Links keep only `http`, `https` and `mailto` targets; anything else
 * (a `javascript:` URL) is shown as its text alone.
 */

export type Dialect = 'legal' | 'guide';

export type Inline =
  | { kind: 'text'; text: string }
  | { kind: 'strong'; children: Inline[] }
  | { kind: 'em'; children: Inline[] }
  | { kind: 'link'; href: string; text: string }
  | { kind: 'code'; text: string };

export type Align = 'left' | 'center' | 'right' | null;

export type Block =
  | { kind: 'heading'; level: 1 | 2 | 3; content: Inline[] }
  | { kind: 'paragraph'; content: Inline[] }
  | { kind: 'list'; ordered: boolean; start: number; items: Inline[][] }
  | { kind: 'quote'; content: Inline[] }
  | { kind: 'rule' }
  | { kind: 'code'; text: string }
  | { kind: 'table'; align: Align[]; header: Inline[][]; rows: Inline[][][] };

const SAFE_HREF = /^(https?:\/\/|mailto:)/i;
const ESCAPABLE = /[\\*_[\]()#>`-]/;
/** A guide's table cells may hold an escaped pipe as well. */
const GUIDE_ESCAPABLE = /[\\*_[\]()#>`|-]/;

/** Bold, italic and links within one block — and, in a guide, inline code. */
export function parseInline(source: string, dialect: Dialect = 'legal'): Inline[] {
  const out: Inline[] = [];
  let text = '';
  const flush = () => {
    if (text) out.push({ kind: 'text', text });
    text = '';
  };

  let i = 0;
  while (i < source.length) {
    const ch = source[i] ?? '';
    const next = source[i + 1] ?? '';

    if (ch === '\\' && (dialect === 'guide' ? GUIDE_ESCAPABLE : ESCAPABLE).test(next)) {
      text += next;
      i += 2;
      continue;
    }

    if (ch === '`' && dialect === 'guide') {
      // Code is literal: nothing inside it is markup, not even an escape.
      const end = source.indexOf('`', i + 1);
      if (end > i + 1) {
        flush();
        out.push({ kind: 'code', text: source.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }

    if (ch === '*' && next === '*') {
      const end = source.indexOf('**', i + 2);
      if (end > i + 2) {
        flush();
        out.push({ kind: 'strong', children: parseInline(source.slice(i + 2, end), dialect) });
        i = end + 2;
        continue;
      }
    }

    if (ch === '*' || ch === '_') {
      // `_` inside a word is part of it (snake_case, an email local part), and a
      // marker followed by a space is a literal asterisk, not an opening.
      const insideWord = ch === '_' && /\w/.test(source[i - 1] ?? '');
      const end = source.indexOf(ch, i + 1);
      if (!insideWord && next !== ' ' && next !== ch && end > i + 1) {
        flush();
        out.push({ kind: 'em', children: parseInline(source.slice(i + 1, end), dialect) });
        i = end + 1;
        continue;
      }
    }

    if (ch === '[') {
      // One level of parentheses inside the target, as a URL may hold.
      const match = /^\[([^\]]+)\]\(((?:[^()\s]|\([^()\s]*\))+)\)/.exec(source.slice(i));
      if (match) {
        flush();
        const [whole, label = '', href = ''] = match;
        out.push(SAFE_HREF.test(href) ? { kind: 'link', href, text: label } : { kind: 'text', text: label });
        i += whole.length;
        continue;
      }
    }

    text += ch;
    i += 1;
  }
  flush();
  return out;
}

const HEADING = /^(#{1,6})\s+(.*?)\s*#*\s*$/;
const RULE = /^\s*([-*_])(\s*\1){2,}\s*$/;
const BULLET = /^\s*[-*+]\s+(.*)$/;
const NUMBERED = /^\s*(\d{1,6})[.)]\s+(.*)$/;
const QUOTE = /^\s*>\s?(.*)$/;
const FENCE = /^\s*```/;
const TABLE_ROW = /^\s*\|/;
const TABLE_DIVIDER = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/;

/** A table row's cells: split on `|` that is not escaped, outer pipes dropped. */
function cells(line: string): string[] {
  const inner = line.trim().replace(/^\|/, '').replace(/(?<!\\)\|$/, '');
  const out: string[] = [];
  let cell = '';
  let code = false;
  for (let i = 0; i < inner.length; i += 1) {
    const ch = inner[i] ?? '';
    if (ch === '\\' && inner[i + 1] === '|') {
      cell += '\\|';
      i += 1;
      continue;
    }
    // A pipe inside `code` belongs to the code, not to the table.
    if (ch === '`') code = !code;
    if (ch === '|' && !code) {
      out.push(cell.trim());
      cell = '';
      continue;
    }
    cell += ch;
  }
  out.push(cell.trim());
  return out;
}

function alignment(cell: string): Align {
  const left = cell.startsWith(':');
  const right = cell.endsWith(':');
  if (left && right) return 'center';
  if (right) return 'right';
  if (left) return 'left';
  return null;
}

/** A whole document, as blocks. */
export function parseDocument(source: string, dialect: Dialect = 'legal'): Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] | null = null;
  let quote: string[] | null = null;
  let list: { ordered: boolean; start: number; items: string[] } | null = null;
  const inline = (text: string) => parseInline(text, dialect);

  const close = () => {
    if (paragraph) blocks.push({ kind: 'paragraph', content: inline(paragraph.join(' ')) });
    if (quote) blocks.push({ kind: 'quote', content: inline(quote.join(' ')) });
    if (list)
      blocks.push({
        kind: 'list',
        ordered: list.ordered,
        start: list.start,
        items: list.items.map(inline),
      });
    paragraph = null;
    quote = null;
    list = null;
  };

  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  for (let index = 0; index < lines.length; index += 1) {
    const raw = lines[index] ?? '';
    const line = raw.replace(/\s+$/, '');

    if (dialect === 'guide' && FENCE.test(line)) {
      close();
      const body: string[] = [];
      index += 1;
      while (index < lines.length && !FENCE.test(lines[index] ?? '')) {
        body.push((lines[index] ?? '').replace(/\s+$/, ''));
        index += 1;
      }
      blocks.push({ kind: 'code', text: body.join('\n') });
      continue;
    }

    if (
      dialect === 'guide' &&
      TABLE_ROW.test(line) &&
      TABLE_DIVIDER.test(lines[index + 1] ?? '')
    ) {
      close();
      const header = cells(line);
      const align = cells(lines[index + 1] ?? '').map(alignment);
      const rows: Inline[][][] = [];
      index += 2;
      while (index < lines.length && TABLE_ROW.test(lines[index] ?? '')) {
        const row = cells(lines[index] ?? '');
        // A short row is padded, a long one cut: a table stays rectangular.
        rows.push(header.map((_, column) => inline(row[column] ?? '')));
        index += 1;
      }
      index -= 1;
      blocks.push({
        kind: 'table',
        align: header.map((_, column) => align[column] ?? null),
        header: header.map(inline),
        rows,
      });
      continue;
    }

    if (line.trim() === '') {
      close();
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      close();
      const level = Math.min(heading[1]?.length ?? 1, 3) as 1 | 2 | 3;
      blocks.push({ kind: 'heading', level, content: inline(heading[2] ?? '') });
      continue;
    }

    if (RULE.test(line)) {
      close();
      blocks.push({ kind: 'rule' });
      continue;
    }

    const bullet = BULLET.exec(line);
    const numbered = bullet ? null : NUMBERED.exec(line);
    if (bullet || numbered) {
      const ordered = numbered !== null;
      const item = (bullet?.[1] ?? numbered?.[2] ?? '').trim();
      const current = list as { ordered: boolean; items: string[] } | null;
      if (current && current.ordered === ordered) {
        current.items.push(item);
      } else {
        close();
        list = { ordered, start: ordered ? Number(numbered?.[1] ?? 1) : 1, items: [item] };
      }
      continue;
    }

    const quoted = QUOTE.exec(line);
    if (quoted) {
      if (!quote) close();
      quote = [...((quote as string[] | null) ?? []), (quoted[1] ?? '').trim()];
      continue;
    }

    // An indented line under a list item carries the item on.
    const openList = list as { items: string[] } | null;
    if (openList && /^\s/.test(raw)) {
      const last = openList.items.length - 1;
      openList.items[last] = `${openList.items[last] ?? ''} ${line.trim()}`;
      continue;
    }

    if (!paragraph) close();
    paragraph = [...((paragraph as string[] | null) ?? []), line.trim()];
  }
  close();
  return blocks;
}
